import React from 'react';
import { useInlineEdit } from '@atlassian/jira-inline-edit-lite/src/ui/use-inline-edit/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useBespokeEmptyFieldTextExperiment } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-experiment/index.tsx';
import ValidationPopup from '@atlassian/jira-issue-field-validation-popup/src/ui/index.tsx';
import type { ValidationPopupProps } from '@atlassian/jira-issue-field-validation-popup/src/ui/types.tsx';
import { EditView } from './edit-view/index.tsx';
import messages from './messages.tsx';
import { ReadView } from './read-view/index.tsx';
import type { FieldInlineEditLiteProps, ValidationFieldProps } from './types.tsx';

// Fallback placements to be used for the validation popover if it overflows its clipping parent
const fallbackPlacements: ValidationPopupProps['fallbackPlacements'] = [
	'left',
	'top-start',
	'auto',
];

/**
 * Component leveraging `@atlassian/jira-inline-edit-lite` to provide consistent spacing and validation UX for issue
 * fields.
 */
const FieldInlineEditLite = ({
	editView,
	editViewPopup,
	editViewPopupAlignBlock,
	editViewPopupMinWidth,
	editViewPopupTarget,
	fieldName,
	hasUnsubmittedChanges,
	hideActionButtons,
	hideEditButton,
	invalidMessage,
	isEditable,
	isEditing,
	keepEditViewOpenOnBlur,
	onCancel,
	onConfirm,
	onEdit,
	onCopy,
	onCut,
	onPaste,
	readViewFitContainerHeight,
	readViewContainerRef,
	readView,
	confirmButtonLabel: confirmButtonLabelProp,
	cancelButtonLabel: cancelButtonLabelProp,
	editButtonLabel: editButtonLabelProp,
	hasReadViewTruncateDisabled,
	value,
}: FieldInlineEditLiteProps) => {
	const { formatMessage } = useIntl();
	const { isExperimentEnabled } = useBespokeEmptyFieldTextExperiment();

	const { context, refs } = useInlineEdit({
		isEditing,
		onCancel,
		onConfirm,
		onEdit,
	});

	const confirmButtonLabel =
		confirmButtonLabelProp ??
		formatMessage(messages.confirmButtonLabel, {
			fieldName,
		});
	const cancelButtonLabel =
		cancelButtonLabelProp ??
		formatMessage(messages.cancelButtonLabel, {
			fieldName,
		});
	const editButtonLabel =
		editButtonLabelProp ??
		formatMessage(messages.editButtonLabel, {
			fieldName,
		});

	if (isEditing && isEditable) {
		return (
			<EditView
				cancelButtonLabel={cancelButtonLabel}
				confirmButtonLabel={confirmButtonLabel}
				context={context}
				editViewPopup={editViewPopup}
				editViewPopupAlignBlock={editViewPopupAlignBlock}
				editViewPopupMinWidth={editViewPopupMinWidth}
				editViewPopupTarget={editViewPopupTarget}
				hideActionButtons={hideActionButtons}
				keepEditViewOpenOnBlur={keepEditViewOpenOnBlur}
			>
				<ValidationPopup
					message={invalidMessage}
					trigger={(triggerProps: ValidationFieldProps) => editView(triggerProps)}
					placement="right"
					fallbackPlacements={fallbackPlacements}
				/>
			</EditView>
		);
	}

	return (
		<ReadView
			context={context}
			editButtonLabel={editButtonLabel}
			editButtonRef={refs.editButton}
			hideEditButton={hideEditButton}
			isEditInteractionEnabled={isEditable}
			isInvalid={hasUnsubmittedChanges}
			readViewFitContainerHeight={readViewFitContainerHeight}
			readViewContainerRef={readViewContainerRef}
			onCopy={onCopy}
			onCut={onCut}
			onPaste={onPaste}
			hasReadViewTruncateDisabled={hasReadViewTruncateDisabled}
			{...(isExperimentEnabled && { value })}
		>
			{readView()}
		</ReadView>
	);
};

export default FieldInlineEditLite;

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { ReadViewNonEditable } from './read-view-non-editable';
